/**
 * offlineSync — Generic offline action queue for non-workout actions.
 * Queues follow/unfollow, reactions, team joins, and challenge submissions
 * when offline, then replays them when connectivity is restored.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform, AppState } from 'react-native';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('OFFLINE_SYNC');
const STORAGE_KEY = 'duel:offline_actions';
const MAX_RETRIES = 5;

export type OfflineActionType =
  | 'follow'
  | 'unfollow'
  | 'reaction'
  | 'team_join'
  | 'challenge_submit';

export interface OfflineAction {
  id: string;
  type: OfflineActionType;
  payload: Record<string, any>;
  createdAt: string;
  retryCount: number;
}

// ── Executor registry ─────────────────────────────────────────────

type ActionExecutor = (payload: Record<string, any>) => Promise<void>;

const executors: Record<OfflineActionType, ActionExecutor> = {
  async follow(p) {
    const { error } = await supabase
      .from('follows')
      .upsert(
        { follower_id: p.followerId, following_id: p.followingId },
        { onConflict: 'follower_id,following_id', ignoreDuplicates: true },
      );
    if (error) throw error;
  },

  async unfollow(p) {
    const { error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', p.followerId)
      .eq('following_id', p.followingId);
    if (error) throw error;
  },

  async reaction(p) {
    // Toggle-on only — if the user toggled off while offline we skip
    const { error } = await supabase
      .from('post_reactions')
      .upsert(
        { post_id: p.postId, user_id: p.userId, reaction_type: p.reactionType ?? 'fire' },
        { onConflict: 'post_id,user_id,reaction_type', ignoreDuplicates: true },
      );
    if (error) throw error;
  },

  async team_join(p) {
    const { error } = await supabase
      .from('team_members')
      .upsert(
        { team_id: p.teamId, user_id: p.userId, role: p.role ?? 'member' },
        { onConflict: 'team_id,user_id', ignoreDuplicates: true },
      );
    if (error) throw error;
  },

  async challenge_submit(p) {
    const { error } = await supabase.from('challenge_submissions').insert({
      challenge_id: p.challengeId,
      user_id: p.userId,
      value: p.value,
      submitted_at: p.submittedAt ?? new Date().toISOString(),
    });
    if (error) throw error;
  },
};

// ── Queue class ───────────────────────────────────────────────────

class OfflineActionQueue {
  private processing = false;
  private initialized = false;

  /** Call once at app startup to wire listeners. */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    // Native: listen for connectivity via NetInfo (optional dep)
    if (Platform.OS !== 'web') {
      try {
        const NetInfo = require('@react-native-community/netinfo').default;
        NetInfo.addEventListener((state: any) => {
          if (state.isConnected) this.processQueue();
        });
      } catch {
        /* NetInfo not installed */
      }
    }

    // Web: listen for online event
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('online', () => this.processQueue());
    }

    // Sync when app comes to foreground
    AppState.addEventListener('change', (s) => {
      if (s === 'active') this.processQueue();
    });

    // Initial attempt
    this.processQueue();
  }

  /** Enqueue an action for later sync. */
  async enqueueAction(type: OfflineActionType, payload: Record<string, any>): Promise<void> {
    const queue = await this.load();
    const action: OfflineAction = {
      id: `oa-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      payload,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };
    queue.push(action);
    await this.save(queue);
    log.info(`Queued offline action: ${type}`, { id: action.id });
  }

  /** Process all pending actions. */
  async processQueue(): Promise<{ synced: number; failed: number }> {
    if (this.processing) return { synced: 0, failed: 0 };
    this.processing = true;

    let synced = 0;
    let failed = 0;

    try {
      const queue = await this.load();
      if (queue.length === 0) return { synced: 0, failed: 0 };

      log.info(`Processing ${queue.length} offline actions`);
      const remaining: OfflineAction[] = [];

      for (const action of queue) {
        try {
          const executor = executors[action.type];
          if (!executor) {
            log.warn(`Unknown action type: ${action.type}, dropping`);
            continue;
          }
          await executor(action.payload);
          synced++;
          log.info(`Synced offline action: ${action.type}`, { id: action.id });
        } catch (err: any) {
          action.retryCount++;
          if (action.retryCount < MAX_RETRIES) {
            remaining.push(action);
          } else {
            log.warn(`Dropping action after ${MAX_RETRIES} retries`, { id: action.id, type: action.type });
          }
          failed++;
        }
      }

      await this.save(remaining);
      if (synced > 0) {
        log.info(`Synced ${synced} actions, ${failed} failed, ${remaining.length} remaining`);
      }
    } finally {
      this.processing = false;
    }

    return { synced, failed };
  }

  /** Number of pending actions (for UI indicator). */
  async getQueueSize(): Promise<number> {
    const queue = await this.load();
    return queue.length;
  }

  /** Clear everything. */
  async clear(): Promise<void> {
    await AsyncStorage.removeItem(STORAGE_KEY);
  }

  // ── Storage helpers ───────────────────────────────────────────

  private async load(): Promise<OfflineAction[]> {
    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private async save(queue: OfflineAction[]): Promise<void> {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  }
}

export const offlineActionQueue = new OfflineActionQueue();

// ── Helpers ─────────────────────────────────────────────────────

/** Returns true if an error looks like a network failure. */
export function isNetworkError(err: any): boolean {
  if (!err) return false;
  const msg = String(err.message ?? err).toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('timeout') ||
    msg.includes('econnrefused') ||
    msg.includes('enotfound') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed')
  );
}
